/**
 * 전역 셸의 이동 정의 — E14 §2 · S0 §8.6.
 *
 * 서브내비 라벨은 그 화면의 제목이고 `document.title`도 같은 라벨을 읽는다(D145 ③).
 * 화면은 제목을 직접 적지 않고 여기서 가져간다.
 */

/** 전역 내비게이션 — L1 §5 최상위 4개 영역 / L2-06 §2 */
export const NAV_ITEMS = [
  { to: '/mylab', label: 'My Lab', gated: true },
  { to: '/funding', label: 'Funding', gated: true },
  { to: '/community', label: 'Community', gated: true },
  { to: '/flow', label: 'Flow', gated: true },
] as const;

export type SubNavGroup = 'daily' | 'loop' | 'time' | 'share';

export interface SubNavEntry {
  key: string;
  label: string;
  to: string;
  group?: string;
  /** 하위 경로에서도 켜지면 안 되는 항목 — `/mylab`(`오늘`) */
  exact?: boolean;
}

/** My Lab 서브내비 — 순서·그룹의 정본은 S0 §8.6이다. */
export const MYLAB_SUBNAV = [
  { key: 'today', label: '오늘', to: '/mylab', group: 'daily', exact: true },
  { key: 'lab-log', label: '연구일지', to: '/mylab/lab-log', group: 'loop' },
  { key: 'meeting', label: '랩미팅', to: '/mylab/meeting', group: 'loop' },
  { key: 'paper', label: '논문', to: '/mylab/paper', group: 'loop' },
  { key: 'schedule', label: '일정', to: '/mylab/schedule', group: 'time' },
  { key: 'upcoming', label: '학회', to: '/mylab/upcoming', group: 'time' },
  { key: 'labs', label: 'Labs', to: '/mylab/labs', group: 'share' },
] as const satisfies ReadonlyArray<SubNavEntry & { group: SubNavGroup }>;

export type MyLabScreen = (typeof MYLAB_SUBNAV)[number]['key'];

/** 계정 목차 — S22 §1. 로그아웃은 이동이 아니라 동작이라 목차 컴포넌트가 따로 붙인다. */
export const ACCOUNT_NAV = [
  { key: 'profile', label: '프로필', to: '/account/profile' },
  {
    key: 'research-interests',
    label: '관심 연구 분야',
    to: '/account/research-interests',
    onboardedOnly: true,
  },
  { key: 'consent', label: '동의 관리', to: '/account/consent' },
] as const;

export type AccountScreen = (typeof ACCOUNT_NAV)[number]['key'];

export function myLabScreenLabel(key: MyLabScreen): string {
  return MYLAB_SUBNAV.find((item) => item.key === key)!.label;
}

export function accountScreenLabel(key: AccountScreen): string {
  return ACCOUNT_NAV.find((item) => item.key === key)!.label;
}

function trimTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

function matches(pathname: string, to: string, exact: boolean): boolean {
  return exact ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
}

/** 지금 주소가 속한 모듈 — 계정 영역·랜딩·온보딩은 어느 모듈도 아니다. */
export function currentModule(pathname: string): (typeof NAV_ITEMS)[number] | undefined {
  const path = trimTrailingSlash(pathname);
  return NAV_ITEMS.find((item) => matches(path, item.to, false));
}

/** 지금 화면의 이름 — 서브내비·계정 목차·모듈 라벨 중 주소와 맞는 것. */
export function currentScreenLabel(pathname: string): string | undefined {
  const path = trimTrailingSlash(pathname);
  const sub =
    MYLAB_SUBNAV.find((item) => 'exact' in item && matches(path, item.to, true)) ??
    MYLAB_SUBNAV.find((item) => !('exact' in item) && matches(path, item.to, false)) ??
    ACCOUNT_NAV.find((item) => matches(path, item.to, false));
  return sub?.label ?? currentModule(path)?.label;
}
